import * as React from 'react';
import {
  Box,
  Link,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { MergeOpportunity, RefreshOpportunity, SeoItem } from '../types';
import ChangeActions from './ChangeActions';
import ChangeStatus from './ChangeStatus';
import { previewDoc } from './ItemDetail';

interface Props {
  item: SeoItem;
  onApprove: (item: SeoItem) => Promise<void>;
  onReject: (item: SeoItem, reason: string) => Promise<void>;
  onRequestChanges: (item: SeoItem, feedback: string) => Promise<void>;
  onUndo: (item: SeoItem) => Promise<void>;
}

// An article refresh: the new and current versions (one at a time) and the
// buttons. What changed and why is listed under "The change".
export default function RefreshDetail({ item, ...actions }: Props) {
  const o = item.opportunity as RefreshOpportunity | MergeOpportunity;
  const retired = o.type === 'merge' ? o.others : [];
  const [shown, setShown] = React.useState<'new' | 'current'>('new');
  React.useEffect(() => setShown('new'), [item.id]);
  const before = item.refreshBefore;
  const doc =
    shown === 'new' || !before
      ? previewDoc(item.draft.html, item.draft.title)
      : previewDoc(before.bodyHtml, before.title);

  return (
    <Stack spacing={2}>
      <ChangeStatus item={item} url={o.url} />
      {!!retired.length && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {item.status === 'published'
              ? 'Retired into this page (unpublished, redirecting here)'
              : 'Pages this retires (unpublished and redirected here on apply)'}
          </Typography>
          <Stack component="ul" sx={{ m: 0, pl: 2 }} spacing={0.5}>
            {retired.map(r => (
              <Typography component="li" variant="body2" key={r.articleId}>
                <Link href={r.url} target="_blank" rel="noopener">
                  {r.title}
                </Link>{' '}
                · {r.impressions.toLocaleString()} impressions,{' '}
                {r.clicks.toLocaleString()} clicks (28 days)
              </Typography>
            ))}
          </Stack>
        </Box>
      )}
      <ChangeActions
        item={item}
        applyText={
          retired.length
            ? `The merged text replaces this article (same URL), and the ${retired.length} other page(s) are unpublished and redirected here. You can undo all of it here at any time.`
            : 'The refreshed text replaces the current article right away (same URL). You can undo it here at any time.'
        }
        undoText={
          retired.length
            ? 'Republishes the retired pages, removes their redirects and puts back this article’s previous heading and text. Stops its remaining checks.'
            : 'Puts back exactly the heading and text that were live before, and stops its remaining checks.'
        }
        {...actions}
      />
      <ToggleButtonGroup
        size="small"
        exclusive
        value={shown}
        onChange={(_, v) => v && setShown(v)}
      >
        <ToggleButton value="new">
          {retired.length ? 'Merged version' : 'Refreshed version'}
        </ToggleButton>
        <ToggleButton value="current" disabled={!before}>
          Version it replaces
        </ToggleButton>
      </ToggleButtonGroup>
      <Box
        component="iframe"
        title={`Preview: ${item.draft.title}`}
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        srcDoc={doc}
        sx={{
          width: '100%',
          height: '70vh',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      />
    </Stack>
  );
}
